/*
    * Created on Wed Oct 11 2023 4:46:23 PM
    *
    * ***************************************************************************************
    * Copyright (c) AVPS Technologies Inc 2023 
    *
    * All Rights Reserved
    * Unpublished rights are reserved under the copyright laws of the United States.
    * The software contained in this media is proprietary to and embodies confidential
    * the technology of AVPS Technologies Inc. Possession, use, duplication or
    * dissemination of the software and media is authorized only pursuant to a valid
    * written license from AVPS Technologies Inc.
    *
    * The above copyright notice and this permission notice shall be included in
    * all copies or substantial portions of the Software.
    *
    * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
    * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
    * 252.227-7013 or in FAR 52.227-19, as applicable.
    ****************************************************************************************
 */
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const CallToActionSection = () => {
  const navigate = useNavigate();

  const handleNavigateToSignup = () => {
    navigate("/sign-up");
  };

  const handleNavigateToSignin = () => {
    navigate("/sign-in");
  };

  return (
    <section
      id="cta-section"
      className="relative bg-gradient-to-br from-slate-50 via-white to-blue-50/30 py-16 px-6 sm:px-8 lg:px-12 overflow-hidden"
      style={{ fontFamily: "var(--flex-font, 'Inter', sans-serif)" }}
    >
      {/* Background Glow Effects */}
      <motion.div
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.15, 0.25, 0.15],
          rotate: [0, 60, 0],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-bl from-blue-400/20 to-transparent rounded-full blur-3xl"
      />
      <motion.div
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1],
          rotate: [0, -45, 0],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-blue-500/10 to-transparent rounded-full blur-3xl"
      />

      {/* Main Content */}
      <div className="relative max-w-4xl mx-auto text-center bg-white/[0.55] backdrop-blur-xl rounded-3xl border border-white/50 shadow-xl px-8 py-12 sm:px-12">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="inline-flex items-center bg-white/60 backdrop-blur-md text-blue-600 px-3 py-1.5 text-xs font-semibold mb-5 border border-blue-200/50 rounded-full"
        >
          Join Our Community
        </motion.span>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          id="cta-heading"
          className="text-3xl md:text-5xl font-bold text-slate-800 mb-4 leading-snug"
        >
          Ready to Book Your
          <br />
          <span className="bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
            Next Workspace?
          </span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          id="cta-description"
          className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto mb-10 font-medium"
        >
          Discover flexible, professional workspaces designed to help you work better—without long-term commitments.
        </motion.p>

        {/* Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row justify-center items-center gap-4"
        >
          {/* Sign Up Button */}
          <motion.button
            id="cta-signup-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNavigateToSignup}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-3 rounded-xl font-semibold text-sm shadow-lg hover:from-blue-700 hover:to-blue-800 hover:shadow-blue-400/40 transition-all duration-300"
          >
            Sign Up Now
          </motion.button>

          {/* Sign In Button */}
          <motion.button
            id="cta-signin-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleNavigateToSignin}
            className="bg-white/50 backdrop-blur-md border border-white/60 text-blue-700 px-8 py-3 rounded-xl font-semibold text-sm shadow-md hover:bg-white/75 transition-all duration-300"
          >
            Already a Member? Sign In
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default CallToActionSection;
